import { useEffect, useState } from 'react';
import Sheet from '../app/components/Sheet';
import Icon from '../app/components/Icon';
import { AI_ACTIONS } from '../app/lib/credits';
import { LOCALE } from '../app/lib/format';
import { useApp } from '../app/lib/AppContext';

const PERIODS = [{ id: 'week', label: 'Tuần' }, { id: 'month', label: 'Tháng' }, { id: 'year', label: 'Năm' }];
const SORTS = [{ id: 'requests', label: 'Nhiều lượt nhất' }, { id: 'tokens', label: 'Nhiều token nhất' }, { id: 'failed', label: 'Lỗi nhiều nhất' }];
const TIERS = { free: 'Free', trial: 'Dùng thử', pro: 'Pro', gift: 'Pro HLV tặng', unlimited: 'Không giới hạn' };
const STATUSES = { success: 'Trừ lượt', free: 'Miễn phí', failed: 'Lỗi', rejected: 'Không liên quan' };
const AREAS = Object.entries(AI_ACTIONS).filter(([id]) => id !== 'ad_reward' && id !== 'admin_grant');
const SELECT = 'select h-11 rounded-[14px] border-line bg-base-100';

const num = (n) => Number(n || 0).toLocaleString(LOCALE);
// Tokens are how much AI work a request was (input + output, thinking included), whatever it's priced at.
const tokens = (n) => (n >= 1e6 ? `${(n / 1e6).toLocaleString(LOCALE, { maximumFractionDigits: 1 })} triệu token` : `${num(n)} token`);
const areaName = (id) => AI_ACTIONS[id]?.label || id;
const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const parse = (s) => { const [y, m, d] = s.split('-').map(Number); return new Date(y, m - 1, d); };
const short = (s) => { const d = parse(s); return `${d.getDate()}/${d.getMonth() + 1}`; };

function periodLabel(p) {
  if (!p) return '';
  const from = parse(p.from);
  if (p.kind === 'year') return `Năm ${from.getFullYear()}`;
  if (p.kind === 'month') return `Tháng ${from.getMonth() + 1}/${from.getFullYear()}`;
  return `Tuần ${short(p.from)} – ${short(p.to)}/${parse(p.to).getFullYear()}`;
}

// Any day in the period before or after this one.
function step(p, dir) {
  const d = dir < 0 ? parse(p.from) : parse(p.to);
  d.setDate(d.getDate() + dir);
  return iso(d);
}

function Stat({ label, value, note }) {
  return (
    <div className="flex flex-col gap-0.5 rounded-[16px] bg-base-100 p-4">
      <span className="text-xs font-semibold uppercase tracking-wide text-muted">{label}</span>
      <span className="tabular text-2xl font-extrabold">{value}</span>
      {note && <span className="text-xs text-muted">{note}</span>}
    </div>
  );
}

const change = (now, before) => {
  if (!before) return now ? 'kỳ trước: 0' : null;
  const pct = Math.round(((now - before) / before) * 100);
  return `${pct >= 0 ? '+' : ''}${pct}% so với kỳ trước`;
};

function Areas({ areas }) {
  return (
    <span className="block truncate text-xs text-muted">
      {Object.entries(areas).sort((a, b) => b[1] - a[1]).map(([id, n]) => `${areaName(id)} ${n}`).join(' · ')}
    </span>
  );
}

// One person in the period: totals, areas and their requests, newest first.
function PersonSheet({ userId, query, onClose }) {
  const { api } = useApp();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => {
    if (!userId) return;
    setData(null);
    setError(null);
    api.get(`/admin/ai_usages/${userId}?${query}`).then(setData).catch((e) => setError(e.message));
  }, [api, userId, query]);

  return (
    <Sheet open={!!userId} onClose={onClose} title={data ? data.user.name : 'Đang tải…'}>
      {error && <p role="alert" className="text-sm text-error">{error}</p>}
      {data && (
        <div className="flex flex-col gap-4">
          <p className="text-sm text-muted">
            {[TIERS[data.user.tier], data.user.phone, data.user.email, periodLabel(data.period)].filter(Boolean).join(' · ')}
          </p>
          <div className="grid grid-cols-3 gap-2">
            <Stat label="Lượt" value={num(data.totals.requests)} />
            <Stat label="Token" value={tokens(data.totals.tokens)} />
            <Stat label="Lỗi" value={num(data.totals.failed + data.totals.rejected)} />
          </div>
          <ul className="flex flex-col divide-y divide-line rounded-[16px] bg-base-200 px-4">
            {data.areas.map((a) => (
              <li key={a.action} className="flex min-h-11 items-center justify-between gap-3 text-sm">
                <span className="font-semibold">{areaName(a.action)}</span>
                <span className="tabular text-muted">{num(a.requests)} lượt · {tokens(a.tokens)}</span>
              </li>
            ))}
          </ul>
          <div className="flex flex-col gap-1">
            <p className="text-xs font-bold uppercase tracking-wide text-muted">Yêu cầu gần nhất</p>
            <ul className="flex flex-col divide-y divide-line">
              {data.requests.map((r) => (
                <li key={r.id} className="flex items-start justify-between gap-3 py-2 text-sm">
                  <span className="min-w-0">
                    <span className="block truncate font-semibold">{areaName(r.action)}{r.label ? ` · ${r.label}` : ''}</span>
                    <span className="block text-xs text-muted">
                      {new Date(r.created_at).toLocaleString(LOCALE, { dateStyle: 'short', timeStyle: 'short' })} · {STATUSES[r.status] || r.status}
                      {r.model ? ` · ${r.model.replaceAll(',', ' + ')}` : ''}{r.error ? ` · ${r.error}` : ''}
                    </span>
                  </span>
                  <span className="tabular shrink-0 font-semibold">{tokens(r.tokens)}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </Sheet>
  );
}

// Admin › Sử dụng AI: who used the AI this week, month or year, ranked, with the tokens each used. No money
// figures: Google's prices and the exchange rate change; tokens × today's price gives the cost when needed.
// Revenue and profit come once the App Store sells Pro.
export default function AiUsagePage() {
  const { api } = useApp();
  const [period, setPeriod] = useState('month');
  const [date, setDate] = useState(iso(new Date()));
  const [area, setArea] = useState('');
  const [sort, setSort] = useState('requests');
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [person, setPerson] = useState(null);
  const query = new URLSearchParams({ period, date, sort, ...(area ? { area } : {}) }).toString();

  useEffect(() => {
    setError(null);
    api.get(`/admin/ai_usages?${query}`).then(setData).catch((e) => setError(e.message));
  }, [api, query]);

  const t = data?.totals;
  // Requests the fallback model answered (all or part of): `model` lists each model a request used.
  const fallback = data ? Object.entries(data.models).filter(([m]) => m && m.split(',').some((x) => !x.includes('lite'))).reduce((s, [, n]) => s + n, 0) : 0;
  return (
    <div className="flex flex-col gap-4">
      <div>
        <h1 className="text-2xl font-bold">Sử dụng AI</h1>
        <p className="text-sm text-muted">
          Ai dùng AI nhiều nhất, vào việc gì. Token là khối lượng AI đã xử lý (nhân với giá hiện tại của Google để ra chi phí); doanh thu và lãi/lỗ sẽ có khi bán Pro qua App Store.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div role="radiogroup" aria-label="Kỳ" className="flex rounded-[14px] bg-base-100 p-1">
          {PERIODS.map((p) => (
            <button key={p.id} type="button" role="radio" aria-checked={period === p.id} onClick={() => setPeriod(p.id)}
              className={`h-9 rounded-[10px] px-4 text-sm font-semibold ${period === p.id ? 'bg-neutral text-neutral-content' : ''}`}>{p.label}</button>
          ))}
        </div>
        <div className="flex items-center gap-1">
          <button type="button" aria-label="Kỳ trước" className="btn btn-ghost btn-circle size-10" disabled={!data} onClick={() => setDate(step(data.period, -1))}>
            <Icon name="chevronLeft" className="size-5" />
          </button>
          <span className="min-w-40 text-center font-bold">{periodLabel(data?.period)}</span>
          <button type="button" aria-label="Kỳ sau" className="btn btn-ghost btn-circle size-10" disabled={!data} onClick={() => setDate(step(data.period, 1))}>
            <Icon name="chevronRight" className="size-5" />
          </button>
        </div>
        <select aria-label="Khu vực" className={SELECT} value={area} onChange={(e) => setArea(e.target.value)}>
          <option value="">Mọi khu vực</option>
          {AREAS.map(([id, a]) => <option key={id} value={id}>{a.label}</option>)}
        </select>
        <select aria-label="Xếp theo" className={SELECT} value={sort} onChange={(e) => setSort(e.target.value)}>
          {SORTS.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
        </select>
      </div>

      {error && <p role="alert" className="text-sm text-error">{error}</p>}
      {!data && !error && <div className="skeleton h-40 rounded-[16px]" />}
      {data && (
        <>
          <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
            <Stat label="Lượt AI" value={num(t.requests)} note={change(t.requests, data.previous.requests)} />
            <Stat label="Người dùng" value={num(t.users)} />
            {/* The fallback model is dearer per token, so how often it answered sits with the tokens. */}
            <Stat label="Token" value={tokens(t.tokens)}
              note={[change(t.tokens, data.previous.tokens) || 'kỳ trước: 0',
                fallback ? `${num(fallback)} lượt dùng model dự phòng` : null].filter(Boolean).join(' · ')} />
            <Stat label="Lỗi / không liên quan" value={num(t.failed + t.rejected)} note={t.free ? `${num(t.free)} lượt miễn phí (tự đổi món)` : null} />
          </div>

          {data.areas.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {data.areas.map((a) => (
                <span key={a.action} className="rounded-full bg-base-100 px-3 py-1.5 text-sm">
                  <b>{areaName(a.action)}</b> {num(a.requests)} · {tokens(a.tokens)}
                </span>
              ))}
            </div>
          )}

          {data.users.length === 0 ? (
            <p className="rounded-[16px] bg-base-100 p-4 text-sm text-muted">Chưa có yêu cầu AI nào trong kỳ này.</p>
          ) : (
            <ol className="flex flex-col divide-y divide-line rounded-[16px] bg-base-100">
              {data.users.map((u) => (
                <li key={u.id}>
                  <button type="button" onClick={() => setPerson(u.id)} className="flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-base-200/60">
                    <span className="tabular w-7 shrink-0 text-center font-bold text-muted">{u.rank}</span>
                    <span className="min-w-0 flex-1">
                      <span className="flex items-center gap-2">
                        <span className="truncate font-bold">{u.name}</span>
                        <span className="shrink-0 rounded-full bg-base-200 px-2 py-0.5 text-[11px] font-semibold">{TIERS[u.tier] || u.tier}</span>
                      </span>
                      <Areas areas={u.areas} />
                    </span>
                    <span className="shrink-0 text-right">
                      <span className="tabular block font-extrabold">{num(u.requests)} lượt</span>
                      <span className="tabular block text-xs text-muted">
                        {tokens(u.tokens)}{u.failed + u.rejected ? ` · ${num(u.failed + u.rejected)} lỗi` : ''}
                      </span>
                    </span>
                  </button>
                </li>
              ))}
            </ol>
          )}
          {data.more && <p className="text-center text-xs text-muted">Chỉ hiện 100 người đầu.</p>}
        </>
      )}
      <PersonSheet userId={person} query={query} onClose={() => setPerson(null)} />
    </div>
  );
}
